import { useCallback } from "react";
import i18n from "i18next";
import { useTokenExpired } from "@context/TokenExpiredProvider";

interface ValidationError {
  error_code: string;
  error_message: string;
  errors?: Array<{
    fields: string[];
    msg: string;
    type: string;
  }>;
}

export interface FieldError {
  key: string;
  message: string;
}

const useHandleError = () => {
  const { showTokenExpired } = useTokenExpired();

  const handleError = useCallback(
    (error: ValidationError): FieldError | void => {
      switch (error.error_code) {
        case "NOT_FOUND":
          return { key: "general", message: i18n.t("error.NOT_FOUND") };
        case "VALIDATION_ERROR":
          return { key: "general", message: i18n.t("error.VALIDATION_ERROR") };
        case "UNAUTHORIZED":
          return { key: "general", message: i18n.t("error.UNAUTHORIZED") };
        case "CONFLICT":
          return { key: "general", message: i18n.t("error.CONFLICT") };
        case "EXISTING_EMAIL":
          return { key: "general", message: i18n.t("error.EXISTING_EMAIL") };
        case "EXISTING_CODE":
          return { key: "general", message: i18n.t("error.EXISTING_CODE") };
        case "LOGIN_NOT_FOUND":
          return { key: "general", message: i18n.t("error.LOGIN_NOT_FOUND") };
        case "PARAMETER_NUMBER_ALREADY_IN_USE":
          return {
            key: "error",
            message: i18n.t("general.PARAMETER_NUMBER_ALREADY_IN_USE"),
          };
        case "INVALID_LOGIN_CREDENTIALS":
          return {
            key: "error",
            message: i18n.t("error.INVALID_LOGIN_CREDENTIALS"),
          };
        case "ACCOUNT_ALREADY_LINKED":
          return {
            key: "error",
            message: i18n.t("error.ACCOUNT_ALREADY_LINKED"),
          };
        case "INVALID_LINKING_CODE":
          return {
            key: "error",
            message: i18n.t("error.INVALID_LINKING_CODE"),
          };
        case "TOKEN_EXPIRED":
          // Instead of redirecting, trigger the token expired modal
          showTokenExpired(i18n.t("error.TOKEN_EXPIRED"));
          return;
        default:
          return { key: "general", message: i18n.t("error.INTERNAL_ERROR") };
      }
    },
    [showTokenExpired]
  );

  return handleError;
};

export default useHandleError;
